import React, { useMemo, useState } from 'react';
import {
  Box, Grid, Card, CardContent, Typography, Alert, Chip, Button, Tooltip,
  CircularProgress,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

function formatBytes(n) {
  if (n === null || n === undefined) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

function formatNumber(n) {
  if (n === null || n === undefined) return '—';
  return n.toLocaleString();
}

function MetricCard({ title, value, errorText, age_seconds }) {
  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="overline" color="text.secondary">{title}</Typography>
        {errorText ? (
          <Alert severity="warning" sx={{ mt: 1 }}>{errorText}</Alert>
        ) : (
          <>
            <Typography variant="h4" sx={{ my: 1 }}>{value}</Typography>
            {age_seconds !== null && age_seconds !== undefined && (
              <Typography variant="caption" color="text.secondary">
                updated {Math.round(age_seconds / 60)} min ago
              </Typography>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

// nginx log severity classification. Most lines in /www/wwwlogs/*.error.log
// look like `2026/05/20 06:07:02 [error] 1304351#0: *731686 ...` — we color by
// the bracketed severity tag.
const SEVERITY_COLORS = {
  emerg: '#ff5252',
  alert: '#ff5252',
  crit: '#ff5252',
  error: '#ef5350',
  warn: '#ffb74d',
  notice: '#90caf9',
  info: '#90a4ae',
  debug: '#78909c',
};
const SEVERITY_RE = /\[(emerg|alert|crit|error|warn|warning|notice|info|debug)\]/i;

function classifyLine(line) {
  const m = SEVERITY_RE.exec(line);
  if (!m) return null;
  const sev = m[1].toLowerCase();
  if (sev === 'warning') return 'warn';
  return sev;
}

function ErrorLogPanel({ tail, errorText }) {
  const [copied, setCopied] = useState(false);

  const lines = useMemo(() => {
    if (!tail) return [];
    return tail.split('\n').filter((l) => l !== '');
  }, [tail]);

  const counts = useMemo(() => {
    const c = { error: 0, warn: 0, notice: 0, other: 0 };
    for (const l of lines) {
      const sev = classifyLine(l);
      if (sev === 'error' || sev === 'emerg' || sev === 'alert' || sev === 'crit') c.error += 1;
      else if (sev === 'warn') c.warn += 1;
      else if (sev === 'notice' || sev === 'info' || sev === 'debug') c.notice += 1;
      else c.other += 1;
    }
    return c;
  }, [lines]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(tail || '');
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // clipboard may not be available; ignore
    }
  };

  return (
    <Card variant="outlined">
      <CardContent sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, flexWrap: 'wrap', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <Typography variant="overline" color="text.secondary">Error log (last 200)</Typography>
            {!errorText && (
              <>
                <Chip size="small" label={`${lines.length} lines`} variant="outlined" />
                {counts.error > 0 && <Chip size="small" label={`${counts.error} errors`} sx={{ bgcolor: SEVERITY_COLORS.error, color: 'common.white' }} />}
                {counts.warn > 0 && <Chip size="small" label={`${counts.warn} warnings`} sx={{ bgcolor: SEVERITY_COLORS.warn, color: 'common.black' }} />}
                {counts.notice > 0 && <Chip size="small" label={`${counts.notice} notices`} variant="outlined" />}
              </>
            )}
          </Box>
          {!errorText && tail && (
            <Tooltip title={copied ? 'Copied!' : 'Copy to clipboard'} arrow>
              <Button
                size="small"
                onClick={handleCopy}
                startIcon={<ContentCopyIcon fontSize="small" />}
                sx={{ textTransform: 'none' }}
              >
                {copied ? 'Copied' : 'Copy'}
              </Button>
            </Tooltip>
          )}
        </Box>

        {errorText ? (
          <Alert severity="warning">{errorText}</Alert>
        ) : (
          <Box
            sx={{
              fontFamily: '"SF Mono", Menlo, Consolas, monospace',
              fontSize: 12,
              lineHeight: 1.6,
              bgcolor: '#0d1117',
              color: '#c9d1d9',
              borderRadius: 1,
              maxHeight: 480,
              overflow: 'auto',
              p: 1.5,
              whiteSpace: 'pre',
            }}
          >
            {lines.length === 0 ? (
              <Box component="span" sx={{ color: '#6e7681' }}>(no error log content)</Box>
            ) : (
              lines.map((line, i) => {
                const sev = classifyLine(line);
                const color = sev && SEVERITY_COLORS[sev] ? SEVERITY_COLORS[sev] : '#c9d1d9';
                return (
                  <Box key={i} sx={{ color, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {line}
                  </Box>
                );
              })
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

export default function HostInfoPanel({ data }) {
  if (data === null || data === undefined) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 4, color: 'text.secondary' }}>
        <CircularProgress size={20} />
        <Typography variant="body2">Loading host telemetry…</Typography>
      </Box>
    );
  }
  if (data.host_provider !== 'nerv' && data.host_provider !== 'wpengine') {
    return (
      <Alert severity="info">
        Host telemetry not configured. Set <strong>Host provider</strong> to <code>nerv</code> or <code>wpengine</code> in the Edit dialog to enable.
      </Alert>
    );
  }

  const stale = data.age_seconds && data.age_seconds > 30 * 60;

  return (
    <Box>
      {data.pending && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Waiting for first collection. The agent runs every 5 minutes.
        </Alert>
      )}
      {stale && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Data is {Math.round(data.age_seconds / 60)} minutes stale.
        </Alert>
      )}
      <Grid container spacing={2}>
        <Grid item xs={12} sm={4}>
          <MetricCard
            title="Disk usage"
            value={formatBytes(data.disk_usage_bytes)}
            errorText={data.disk_error}
            age_seconds={data.age_seconds}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <MetricCard
            title="Bandwidth (24h)"
            value={formatBytes(data.bandwidth_24h_bytes)}
            errorText={data.bandwidth_error}
            age_seconds={data.age_seconds}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <MetricCard
            title="Requests (24h)"
            value={formatNumber(data.request_count_24h)}
            errorText={data.requests_error}
            age_seconds={data.age_seconds}
          />
        </Grid>
        <Grid item xs={12}>
          <ErrorLogPanel tail={data.error_log_tail} errorText={data.error_log_error} />
        </Grid>
      </Grid>
    </Box>
  );
}
